import React, {useEffect, useState} from "react";
import {pageAuthorizedByGroup} from "../../fragments/PageAuthorization";
import {Link, useParams} from "react-router-dom";
import {
	Box,
	Breadcrumbs, Checkbox,
	CircularProgress, FormControlLabel, Paper, Stack,
	TextField,
	Typography
} from "@mui/material";
import {PentaSplit} from "../../../components/common/PentaSplit";
import {
	COMPARATOR_CONFIG_DETAILS,
	PRODUCT_INDEX,
	PUT_COMPARATOR_CONFIG,
	SUPPLIER_INDEX
} from "../Queries";
import {graphQLServices} from "../../../services/GraphQLServices";
import {useQuery} from "@apollo/client";
import {ExpandMore} from "@mui/icons-material";
import {ProcessButton} from "../../../components/common/input/ProcessButton";
import {cloneMutationData} from "../Utils";
import {productsEditorDefaultTargetStage, productsEditorTargetStage} from "../ProductModuleOverview";
import {Product} from "../models/Product";
import {ComparatorConfig} from "../models/ComparatorConfig";
import {ProductSupplier} from "../models/ProductSupplier";
import {ComparatorConfigSupplier} from "../models/ComparatorConfigSupplier";
import {LazyAccordion} from "../../../components/common/containers/LazyAccordion";
import {ClickIsolation} from "../../../components/common/input/ClickIsolation";
import {ComparatorConfigProduct} from "../models/ComparatorConfigProduct";
import {translateFunction} from "../../../localization/TranslationService";
import {HeaderSlot, StageSwitch} from "../../../components/onsite/Footer";
import {GlobalState} from "../../../global/GlobalState";
import {targetStageSafetyCheck} from "../../../libraries/util/SafetyCheck";
import {AfmLicense} from "./AfmLicense";

const translate = translateFunction("SureBasePortalProductsCatalog");

export const ComparatorConfigsDetails = pageAuthorizedByGroup(function () {
	const [tenant, comparatorId] = useParams()?.configId?.split(":") ?? [];
	const fullKrn = `krn:${tenant}:comparator-config:${comparatorId}`;
	
	const stage = GlobalState.targetKiziStage(productsEditorDefaultTargetStage);
	const disabled = stage !== productsEditorTargetStage;
	
	const comparatorQuery = useQuery(COMPARATOR_CONFIG_DETAILS, {
		client: graphQLServices.productsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage,
			tenant,
			comparatorId
		}
	});
	
	const supplierIndex = useQuery(SUPPLIER_INDEX, {
		client: graphQLServices.productsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage,
		}
	});
	
	const productIndex = useQuery(PRODUCT_INDEX, {
		client: graphQLServices.productsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage,
			tenant,
			productTypeKrns: availableProductTypeKrns(comparatorId),
		}
	});
	
	const [comparatorConfig, setComparatorConfig] = useState<ComparatorConfig>();
	
	// reset the state when stage changes
	// todo change this to proper effect that sets the state instead of clears the state, after removing the newProduct feature
	useEffect(() => {
		setComparatorConfig(undefined);
	}, [stage]);
	
	const error = comparatorQuery.error || supplierIndex.error || productIndex.error;
	if (error) {
		throw error;
	}
	
	const loading = comparatorQuery.loading || supplierIndex.loading || productIndex.loading;
	if (loading)
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	
	if (error)
		throw error;
	
	const nodes = comparatorQuery.data.comparatorConfigs.nodes;
	if (nodes.length === 0)
		throw new Error(`Comparator Config with krn ${fullKrn} is not found.`);
	
	const suppliers: ProductSupplier[] = supplierIndex.data?.productSuppliers?.nodes ?? [];
	const suppliersById = suppliers.associateBy(it => it.id);
	const products: Product[] = productIndex.data?.products?.nodes ?? [];
	const productsById = products.associateBy(it => it.id);
	
	if (comparatorConfig && !comparatorConfig.afmLicense) {
		setComparatorConfig({...comparatorConfig, afmLicense: {name: "", isUrl: false, url: "", number: ""}});
	}
	
	const node: ComparatorConfig = nodes[0];
	if (!comparatorConfig) {
		setComparatorConfig(enrichData(node, suppliers, products));
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	}
	
	async function saveChanges(comparatorConfig: ComparatorConfig) {
		targetStageSafetyCheck(stage);
		
		await graphQLServices.productsClient.mutate({
			mutation: PUT_COMPARATOR_CONFIG,
			variables: {
				stage,
				comparatorConfig: cloneMutationData(trimUnnecessaryData(comparatorConfig)),
			}
		});
	}
	
	const props = {comparatorConfig, setComparatorConfig, suppliersById, productsById, disabled};
	return <>
		<HeaderSlot><StageSwitch defaultTargetStage={productsEditorDefaultTargetStage}/></HeaderSlot>
		<PentaSplit
			top={<Breadcrumbs style={{margin: "5px"}}>
				<Link style={{color: "inherit", textDecoration: "unset"}} to="/products">{translate("Products")}</Link>
				<Link style={{color: "inherit", textDecoration: "unset"}}
					to="/products/comparatorconfigs">{translate("Comparator Configs")}</Link>
				<Typography color="text.primary">{tenant + ":" + comparatorId}</Typography>
			</Breadcrumbs>}
			center={<Paper style={{
				height: "100%",
			}}>
				<Stack style={{height: "100%"}}>
					<AfmLicense {...props}></AfmLicense>
					<Information {...props}/>
					<div style={{flexGrow: 0, padding: "10px"}}>
						<ProcessButton
							buttonProps={{disabled}}
							defaultText={translate("Save")}
							inProgressText={translate("Saving")}
							completedText={translate("Saved")}
							resetKeys={[comparatorConfig]}
							variant={"outlined"}
							onClick={() => saveChanges(comparatorConfig)}/>
					</div>
				</Stack>
			</Paper>}
		/>
	</>;
}, ["Admin", "Product"]);

function availableProductTypeKrns(comparatorId: string): string[] {
	switch (comparatorId) {
		case "liability":
			return [
				"krn:kizi:product-type:insurance/damage/liability",
			];
		case "healthcare":
			return [
				"krn:kizi:product-type:insurance/healthcare",
				"krn:kizi:product-type:insurance/healthcare/budget",
			];
		case "travel-multitrip":
			return [
				"krn:kizi:product-type:insurance/damage/travel",
			];
		case "triple-play":
			return [
				"krn:kizi:product-type:insurance/triple-play",
				"krn:kizi:product-type:contract/triple-play"
			];
		case "building-home":
			return [
				"krn:kizi:product-type:insurance/package",
				"krn:kizi:product-type:insurance/building-household-package",
				"krn:kizi:product-type:insurance/damage/home",
				"krn:kizi:product-type:insurance/damage/household",
				"krn:kizi:product-type:insurance/damage/building",
				"krn:kizi:product-type:insurance/damage/liability",
			];
		case "solar-panels":
			return [
				"krn:kizi:product-type:contract/solar-panels"
			];
		case "savings":
			return [
				"krn:kizi:product-type:banking/savings"
			];
		case "event-based":
			return [
				"krn:kizi:product-type:insurance/event-based"
			];
		case "car":
			return [
				"krn:kizi:product-type:insurance/car"
			];
		case "business-car":
			return [
				"krn:kizi:product-type:insurance/business-car"
			];
		case "business":
			return [
				"krn:kizi:product-type:business/car",
				"krn:kizi:product-type:business/van",
				"krn:kizi:product-type:business/businessdamage",
				"krn:kizi:product-type:business/additionalcosts",
				"krn:kizi:product-type:business/legal",
				"krn:kizi:product-type:business/computer_electronics",
				"krn:kizi:product-type:business/liability",
				"krn:kizi:product-type:business/inventory",
				"krn:kizi:product-type:business/building",
				"krn:kizi:product-type:business/accident"
			];
		default:
			return [];
	}
}

function trimUnnecessaryData(comparatorConfig: ComparatorConfig): ComparatorConfig {
	return {
		...comparatorConfig,
		suppliers: comparatorConfig.suppliers
			?.map(supplier => ({
				supplierId: supplier.supplierId,
				supplierKrn: supplier.supplierKrn,
				canCommit: supplierCanCommit(supplier),
				canCalculate: supplierCanCalculate(supplier),
				productExclusions: supplier.productExclusions
					?.filter(product => productIsDifferent(supplier, product))
					?.map(product => ({
						productId: product.productId,
						productKrn: product.productKrn,
						visible: productCanCalculate(supplier, product),
					} as ComparatorConfigProduct)) ?? [],
			}))
			?.filter(supplier => supplier.canCalculate || supplier.canCommit) ?? [],
	};
}

/**
 * enriches comparator config data to include all suppliers and all their products
 * @param comparatorConfig base config to copy visible, canCalculate, and canCommit from
 * @param suppliers all suppliers to be added
 * @param products all products to be added
 */
function enrichData(
	comparatorConfig: ComparatorConfig,
	suppliers: ProductSupplier[],
	products: Product[],
): ComparatorConfig {
	const productsById = products.associateManyBy(it => it.productSupplierId);
	
	const configSuppliers = comparatorConfig.suppliers ?? [];
	const configSuppliersById = configSuppliers.associateBy(it => it.supplierId);
	
	return {
		...comparatorConfig,
		suppliers: suppliers.map(supplier => {
			const configSupplier = configSuppliersById.get(supplier.id);
			const products = productsById.get(supplier.id) ?? [];
			const configProducts = (configSupplier?.productExclusions ?? []).associateBy(it => it.productId);
			return {
				supplierId: supplier.id,
				supplierKrn: supplier.krn,
				canCalculate: configSupplier?.canCommit || (configSupplier?.canCalculate ?? false),
				canCommit: configSupplier?.canCommit ?? false,
				productExclusions: products.map(product => {
					const configProduct = configProducts.get(product.id);
					return {
						productId: product.id,
						productKrn: product.krn,
						visible: configProduct?.visible ?? true,
						canCommit: configProduct === undefined,
					};
				}),
			};
		}),
	};
}

interface InformationProps {
	readonly comparatorConfig: ComparatorConfig;
	readonly setComparatorConfig: (config: ComparatorConfig) => void;
	readonly suppliersById: Map<string, ProductSupplier>;
	readonly productsById: Map<string, Product>;
	readonly disabled?: boolean;
}

function Information(props: InformationProps) {
	const fullKrn = `krn:${props.comparatorConfig.clientName}:comparator-config:${props.comparatorConfig.comparatorId}`;
	const configSuppliers = props.comparatorConfig.suppliers ?? [];
	
	return <div style={{height: "100%", overflow: "auto"}}>
		<Stack spacing={2} sx={{p: 3}}>
			<div>{translate("Product Information")}</div>
			<TextField
				label={translate("Kizi Resource Name")}
				value={fullKrn}
				variant={"filled"}
				disabled
			/>
			
			<div>
				{configSuppliers.map((supplier, index) => {
					if (supplier.productExclusions === null || supplier.productExclusions.length === 0)
						return undefined;
					
					const dbSupplier = props.suppliersById.get(supplier.supplierId);
					if (dbSupplier === undefined)
						return undefined;
					
					function setSupplier(supplier: ComparatorConfigSupplier) {
						const newSuppliers = [...configSuppliers];
						newSuppliers[index] = supplier;
						const newConfig = {...props.comparatorConfig, suppliers: newSuppliers};
						props.setComparatorConfig(newConfig);
					}
					
					return <InformationContent
						supplier={supplier}
						dbSupplier={dbSupplier}
						index={index}
						productsById={props.productsById}
						setSupplier={setSupplier}
						disabled={props.disabled}
					/>;
				}).filter(it => !!it)}
			</div>
		</Stack>
	</div>;
}

function InformationContent(props: {
	readonly supplier: ComparatorConfigSupplier;
	readonly dbSupplier: ProductSupplier;
	readonly index: number;
	readonly productsById: Map<string, Product>;
	readonly setSupplier: (supplier: ComparatorConfigSupplier) => void;
	readonly disabled?: boolean;
}) {
	const supplier = props.supplier;
	const dbSupplier = props.dbSupplier;
	const index = props.index;
	const productExclusions = supplier.productExclusions ?? [];
	
	return <LazyAccordion
		containerProps={{key: supplier.supplierId}}
		summaryProps={{expandIcon: <ExpandMore/>}}
		summary={<Stack direction={"row"} key={index} spacing={2} alignItems={"center"}>
			<ClickIsolation>
				<TextField
					label={translate("Supplier KRN")}
					style={{width: "300px"}}
					value={supplier.supplierKrn ?? ""}
					variant={"standard"}
					disabled
				/>
			</ClickIsolation>
			<ClickIsolation>
				<TextField
					label={translate("Name")}
					style={{width: "300px"}}
					value={dbSupplier.legalName ?? ""}
					variant={"standard"}
					disabled
				/>
			</ClickIsolation>
			<ClickIsolation>
				<FormControlLabel
					control={<Checkbox
						checked={supplierCanCommit(supplier)}
						onChange={event => {
							const newSupplier = {...supplier, canCommit: event.target.checked};
							props.setSupplier(newSupplier);
						}}
					/>}
					disabled={props.disabled}
					label={translate("Can Commit")}/>
			</ClickIsolation>
			<ClickIsolation>
				<FormControlLabel
					control={<Checkbox
						checked={supplierCanCalculate(supplier)}
						onChange={event => {
							const newSupplier = {...supplier, canCalculate: event.target.checked};
							props.setSupplier(newSupplier);
						}}
					/>}
					disabled={props.disabled || supplier.canCommit}
					label={translate("Can Calculate")}/>
			</ClickIsolation>
			<div>{translate("Products:")} {productExclusions.length}</div>
		</Stack>}
	>
		<Stack spacing={2}>
			{productExclusions.map((product, index) => {
				const dbProduct = props.productsById.get(product.productId);
				if (dbProduct === undefined)
					return undefined;
				
				function setProduct(product: ComparatorConfigProduct) {
					const newProducts = [...productExclusions];
					newProducts[index] = product;
					const newSupplier = {...supplier, productExclusions: newProducts};
					props.setSupplier(newSupplier);
				}
				
				return <Stack key={product.productId} direction={"row"} spacing={2}
					style={{paddingLeft: "20px", paddingRight: "20px"}}>
					<TextField
						label={translate("Product KRN")}
						style={{flexGrow: "4"}}
						value={product.productKrn ?? ""}
						variant={"standard"}
						disabled
					/>
					<TextField
						label={translate("Name")}
						style={{flexGrow: "3"}}
						value={dbProduct.name ?? ""}
						variant={"standard"}
						disabled
					/>
					<FormControlLabel
						control={<Checkbox
							checked={productCanCommit(supplier, product)}
							onChange={event => {
								const newProduct = {...product, canCommit: event.target.checked};
								setProduct(newProduct);
							}}
						/>}
						disabled={props.disabled || !supplier.canCommit}
						label={translate("Can Commit")}/>
					<FormControlLabel
						control={<Checkbox
							checked={productCanCalculate(supplier, product)}
							onChange={event => {
								const newProduct = {...product, visible: event.target.checked};
								setProduct(newProduct);
							}}
						/>}
						disabled={props.disabled || !supplierCanCalculate(supplier) || (supplier.canCommit && product.canCommit)}
						label={translate("Can Calculate")}/>
				</Stack>;
			}).filter(it => !!it)}
		</Stack>
	</LazyAccordion>;
}

function supplierCanCommit(supplier: ComparatorConfigSupplier) {
	return supplier.canCommit;
}

function supplierCanCalculate(supplier: ComparatorConfigSupplier) {
	return supplier.canCommit || supplier.canCalculate;
}

function productCanCommit(supplier: ComparatorConfigSupplier, product: ComparatorConfigProduct) {
	return supplierCanCommit(supplier) && product.canCommit;
}

function productCanCalculate(supplier: ComparatorConfigSupplier, product: ComparatorConfigProduct) {
	return supplierCanCalculate(supplier) && (product.canCommit || product.visible);
}

function productIsDifferent(supplier: ComparatorConfigSupplier, product: ComparatorConfigProduct) {
	return supplierCanCommit(supplier) !== productCanCommit(supplier, product)
		|| supplierCanCalculate(supplier) !== productCanCalculate(supplier, product);
}

export function openComparatorConfigDetailsFor(krn: string) {
	window.location.href = linkComparatorConfigDetailsFor(krn);
}

export function linkComparatorConfigDetailsFor(krn: string): string {
	const split = krn.split(":");
	const tenant = split[1];
	const comparatorId = split[3];
	return `/products/comparatorconfigs/${encodeURIComponent([tenant, comparatorId].join(":"))}`;
}
